import { useEffect, useMemo, useState } from 'react';
import { SearchableSelect } from '../../../ui';
import { fetchAdsAccounts, fetchLinkedAccounts, fmtCustomerId, type AdsAccount } from '../../../../lib/googleAds';

interface AdsAccountPickerProps {
  clientId: string;
  clientName: string;
  customerId: string | null;
  onPick: (customerId: string | null, label: string | null) => void;
  style?: React.CSSProperties;
}

const squash = (s: string) => s.toLowerCase().replace(/^the\s+/, '').replace(/[^a-z0-9]/g, '');

/**
 * Which Google Ads account under the Bullfinch MCC is this client's. Accounts whose name looks like the client sort
 * first; one already linked to another client says so, but can still be picked.
 */
export default function AdsAccountPicker({ clientId, clientName, customerId, onPick, style }: AdsAccountPickerProps) {
  const [accounts, setAccounts] = useState<AdsAccount[] | null>(null);
  const [linked, setLinked] = useState<Map<string, { id: string; name: string }>>(new Map());
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    Promise.all([fetchAdsAccounts(), fetchLinkedAccounts()])
      .then(([list, links]) => {
        if (!live) return;
        setAccounts(list);
        setLinked(links);
      })
      .catch((err) => {
        console.error('Error loading Google Ads accounts:', err);
        if (live) setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      live = false;
    };
  }, []);

  const options = useMemo(() => {
    const name = squash(clientName);
    const looksLike = (a: AdsAccount) => {
      const s = squash(a.name);
      return name.length >= 3 && s.length >= 3 && (s.includes(name) || name.includes(s));
    };
    const list = (accounts ?? []).map((a) => {
      const other = linked.get(a.id);
      const suggested = looksLike(a);
      const hint =
        other && other.id !== clientId ? `Linked to ${other.name}` : suggested && a.id !== customerId ? 'Looks like a match' : fmtCustomerId(a.id);
      return { value: a.id, label: a.name || fmtCustomerId(a.id), hint, suggested };
    });
    list.sort((a, b) => Number(b.suggested) - Number(a.suggested) || a.label.localeCompare(b.label));
    if (customerId && accounts && !list.some((o) => o.value === customerId)) {
      list.unshift({ value: customerId, label: fmtCustomerId(customerId), hint: 'Not under the MCC', suggested: false });
    }
    return list.map(({ value, label, hint }) => ({ value, label, hint }));
  }, [accounts, linked, clientId, clientName, customerId]);

  if (loadError) {
    return (
      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--status-warn)', ...style }} title={loadError}>
        Couldn’t load Google Ads accounts
      </span>
    );
  }

  return (
    <SearchableSelect
      options={options}
      value={customerId}
      onChange={(value) => value !== customerId && onPick(value, options.find((o) => o.value === value)?.label ?? null)}
      allowClear={customerId != null}
      clearLabel="No Google Ads account"
      placeholder={accounts ? 'Pick Google Ads account…' : 'Loading Google Ads…'}
      searchPlaceholder="Search accounts..."
      size="sm"
      menuMinWidth={320}
      style={style}
    />
  );
}
